import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { IbdEvents } from '@theme/_ibd-events';
import {
  RATINGS_WIDGET_DEFAULT_SETTINGS,
  RATINGS_WIDGET_SCRIPT_URL,
  RATINGS_WIDGET_CSS_URL,
} from '@theme/_ibd-reviewsio-settings';

/** @typedef {import('@theme/events').VariantUpdateEvent} VariantUpdateEvent */
/** @typedef {import('@theme/_ibd-utilities').IbdSkeleton} IbdSkeleton */
/** @typedef {import('@theme/_ibd-events').IbdProductUpdateEvent} IbdProductUpdateEvent */
/** @typedef {import('@theme/_ibd-reviewsio-types').RatingsWidgetSettings} RatingsWidgetSettings */

/**
 * IBD Product Ratings Component
 *
 * Displays Reviews.io rating snippets for products.
 *
 */
class IbdProductRatings extends Component {
  /** @type {MutationObserver|null} */
  settingsObserver = null;

  /** @type {MutationObserver|null} */
  widgetObserver = null;

  /** @type {number} */
  maxAttempts = 50;

  /** @type {number} */
  attempts = 0;

  /** @type {number|null} */
  timeout = null;

  /** @type {RatingsWidgetSettings} */
  settings = RATINGS_WIDGET_DEFAULT_SETTINGS;

  /** @type {boolean} */
  widgetLoaded = false;

  /** @type {IbdSkeleton|null} */
  #skeleton = null;

  /** @type {((event: IbdProductUpdateEvent | VariantUpdateEvent) => void)|null} */
  #boundHandleVariantUpdate = null;

  constructor() {
    super();
    this.#injectStylesheet();
    this.#injectScript();
    this.#skeleton = this.querySelector('ibd-skeleton');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#reset();
    this.#refreshSkeleton();
    this.#loadSettings();
    this.#loadRatingSnippet();
    this.#observeWidget();
    this.#bindEventListeners();
  }

  /**
   * Called when the element is updated via morph.
   * Refreshes the skeleton reference in case it was replaced.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#refreshSkeleton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reset();
    this.#unbindEventListeners();
    this.#disconnectWidgetObserver();
  }

  #loadSettings() {
    /** @type {RatingsWidgetSettings|undefined} */
    let settings;

    try {
      const customSettings = JSON.parse(this.dataset.settings ?? '{}');
      settings = { ...this.settings, ...customSettings };
    } catch (error) {
      console.error('IBD | Product Ratings | #loadSettings | Error parsing settings', error);
      settings = this.settings;
    }

    if (!settings || !('store' in settings) || !settings.store) {
      throw new Error('IBD | Product Ratings | #loadSettings | No store found in settings');
    }

    this.settings = settings;
  }

  #injectScript() {
    const hasScript = document.querySelector(`script[src="${RATINGS_WIDGET_SCRIPT_URL}"]`) !== null;
    if (hasScript) {
      return;
    }

    const script = document.createElement('script');
    script.src = RATINGS_WIDGET_SCRIPT_URL;
    script.defer = true;
    document.head.appendChild(script);
  }

  #injectStylesheet() {
    const hasStylesheet = document.querySelector(`link[href="${RATINGS_WIDGET_CSS_URL}"]`) !== null;
    if (hasStylesheet) {
      return;
    }

    const link = document.createElement('link');
    link.href = RATINGS_WIDGET_CSS_URL;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }

  #loadRatingSnippet() {
    this.attempts++;

    // Check for ratingSnippet function (loaded from external script)
    // @ts-ignore - ratingSnippet is loaded from external script
    const ratingSnippetFn = window.ratingSnippet || (typeof ratingSnippet !== 'undefined' ? ratingSnippet : null);

    if (typeof ratingSnippetFn === 'function') {
      if (this.timeout) clearTimeout(this.timeout);
      ratingSnippetFn('ruk_rating_snippet', this.settings);
    } else if (this.attempts < this.maxAttempts) {
      this.timeout = setTimeout(() => this.#loadRatingSnippet(), 200);
    } else {
      console.error(`IBD | Product Ratings | #loadRatingSnippet | Failed to load after ${this.maxAttempts} attempts`);
    }
  }

  /**
   * Observes the widget container for content changes to detect when Reviews.io populates it.
   */
  #observeWidget() {
    /** @type {HTMLElement|null} */
    const widgetContainer = this.querySelector('.ruk_rating_snippet');
    if (!widgetContainer) {
      // Widget container not found yet, try again after a short delay
      setTimeout(() => this.#observeWidget(), 100);
      return;
    }

    // Disconnect any existing observer
    this.#disconnectWidgetObserver();

    // Create observer to watch for data-done attribute
    this.widgetObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Check if data-done attribute was set to "true"
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-done') {
          if (widgetContainer.getAttribute('data-done') === 'true' && !this.widgetLoaded) {
            this.widgetLoaded = true;
            this.#skeleton?.resetShimmer();
            this.#freezeWidget();
          }
        }
      }
    });

    // Observe the widget container for data-done attribute changes
    this.widgetObserver.observe(widgetContainer, {
      attributes: true,
      attributeFilter: ['data-done'],
    });

    // Also check if data-done is already set (in case widget loaded before observer)
    if (widgetContainer.getAttribute('data-done') === 'true' && !this.widgetLoaded) {
      this.widgetLoaded = true;
      this.#skeleton?.resetShimmer();
      this.#freezeWidget();
    }
  }

  /**
   * Disconnects the widget observer.
   */
  #disconnectWidgetObserver() {
    if (this.widgetObserver) {
      this.widgetObserver.disconnect();
      this.widgetObserver = null;
    }
  }

  /**
   * Refreshes the skeleton element reference.
   * Should be called after morph/updates to ensure we have the current skeleton element.
   */
  #refreshSkeleton() {
    /** @type {IbdSkeleton|null} */
    this.#skeleton = this.querySelector('ibd-skeleton');
  }

  #reset() {
    this.settings = RATINGS_WIDGET_DEFAULT_SETTINGS;
    this.attempts = 0;
    this.widgetLoaded = false;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.#disconnectWidgetObserver();
    this.#unfreezeWidget();
    this.#skeleton = null;
    this.#boundHandleVariantUpdate = null;
  }

  /**
   * Freezes the widget container, to prevent Reviews.io functions from targeting it.
   * ! This is a workaround to prevent the widget from being updated when product cards around the page
   * ! are being loaded dynamically, which seems to call the ratingsSnippet function on all .ruk_rating_snippet
   * ! elements on the page, which in turn makes some widgets to get the wrong data.
   */
  #freezeWidget() {
    const widgetContainer = this.querySelector('[data-product-ratings-widget]');
    widgetContainer?.classList.remove('ruk_rating_snippet');
  }

  /**
   * Unfreezes the widget container, to allow Reviews.io functions to target it again.
   */
  #unfreezeWidget() {
    const widgetContainer = this.querySelector('[data-product-ratings-widget]');
    widgetContainer?.classList.add('ruk_rating_snippet');
  }

  /**
   * Handles a variant update event.
   *
   * @param {IbdProductUpdateEvent | VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate(event) {
    if (!event.detail.data || !event.detail.data.html) {
      console.error('IBD | Product Ratings | #handleVariantUpdate | No data or HTML found in event detail');
      return;
    }

    /** @type {HTMLElement|null} */
    const newElement = event.detail.data.html.querySelector('ibd-product-ratings');
    if (!newElement) {
      console.error('IBD | Product Ratings | #handleVariantUpdate | Rating element not found in html data');
      return;
    }

    // Reset widget loaded state so we can detect when the new widget loads
    this.widgetLoaded = false;
    this.#refreshSkeleton();
    this.#loadSettings();
    this.#observeWidget();
    this.#loadRatingSnippet();
  }

  #bindEventListeners() {
    this.#boundHandleVariantUpdate = (event) => this.#handleVariantUpdate(event);

    const closestSection = this.closest('.shopify-section, dialog, product-card');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
    document.addEventListener(IbdEvents.ibdProductUpdated, this.#boundHandleVariantUpdate);
  }

  #unbindEventListeners() {
    if (this.#boundHandleVariantUpdate) {
      const closestSection = this.closest('.shopify-section, dialog, product-card');
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#boundHandleVariantUpdate);
      document.removeEventListener(IbdEvents.ibdProductUpdated, this.#boundHandleVariantUpdate);

      this.#boundHandleVariantUpdate = null;
    }
  }
}

if (!customElements.get('ibd-product-ratings')) {
  customElements.define('ibd-product-ratings', IbdProductRatings);
}
